/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

// NOTE: This entity display does not support Tabular Data at this time.
class CustomInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		this.onChangeCallbacks = [];
	}

	getSelectInputHTMLID(pInputHTMLID)
	{
		return `#BRIDGE-SELECT-FOR-${pInputHTMLID}`;
	}

	getTabularSelectInputID(pInputHTMLID, pRowIndex)
	{
		return `#BRIDGE-SELECT-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularSelectDropdownID(pInputHTMLID, pRowIndex)
	{
		return `#BRIDGE-SELECT-TABULAR-DROPDOWN-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * Adds an onChange callback to the input handler.
	 * @param {function} pCallback The callback function.
	 */
	addOnChangeCallback(pCallback)
	{
		if (!pCallback || typeof pCallback !== 'function')
		{
			this.pict.log.error('Invalid onChangeCallback provided.');
			return;
		}
		// check if the callback is already in the list
		if (this.onChangeCallbacks.indexOf(pCallback) > -1)
		{
			this.pict.log.warn('onChangeCallback already exists in the list.');
			return;
		}
		this.onChangeCallbacks.push(pCallback);
	}

	/**
	 * Temporary loading skeleton for the input element.
	 * @param {object} pInput The input object.
	 */
	addLoadingSkeleton(pInput)
	{
		if (!this.pict?.AppData?.Interactive || this.pict?.AppData?.DocumentData?.ReportData?.Locked)
		{
			return;
		}
		const tmpLoadingBox = document.createElement('div');
		tmpLoadingBox.id = `LOADING-${pInput.Macro.RawHTMLID}`;
		tmpLoadingBox.classList.add('input', 'is-skeleton');
		tmpLoadingBox.style.left = '0';
		tmpLoadingBox.style.position = 'absolute';
		tmpLoadingBox.style.opacity = '0.5';
		this.pict.ContentAssignment.getElement(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID))[0]?.parentElement?.appendChild(tmpLoadingBox);
		this.pict.ContentAssignment.addClass(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID), 'is-hidden');
	}

	/**
	 * Remove the loading skeleton from the input element.
	 * @param {object} pInput The input object.
	 */
	removeLoadingSkeleton(pInput)
	{
		const tmpLoadingBox = this.pict.ContentAssignment.getElement(`#LOADING-${pInput.Macro.RawHTMLID}`);
		if (tmpLoadingBox && tmpLoadingBox.length > 0)
		{
			tmpLoadingBox[0].remove();
		}
		this.pict.ContentAssignment.removeClass(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID), 'is-hidden');
	}

	/**
	 * Initialize the Select2 plugin for the input element.
	 * @param {object} pInput The input object.
	 * @param {string} pStateFilter The state fitler string.
	 */
	initializeSelect2(pInput, pStateFilter)
	{
		if (window['jQuery'] && window['jQuery'].fn.select2)
		{
			const tmpSelectOptions = this.pict.providers.Select2Util.getOptionsAjax('Bridge',
				{
					Input: pInput,
					SearchFields: [ 'StructureNumber', 'Name' ],
					AllowClear: false,
					SearchFallbackPlaceholder: 'Select a Bridge by name or code...',
					HandleSearchFilters: (pFilters) =>
					{
						if (!pStateFilter)
						{
							return pFilters;
						}
						if (pFilters.length > 0)
						{
							pFilters.unshift('FOP~0~(~0');
							pFilters.push('FCP~0~)~0');
						}
						if (Array.isArray(pStateFilter))
						{
							pFilters.push(`FBL~State~INN~${pStateFilter.join(',')}`);
						}
						else
						{
							pFilters.push(`FBV~State~EQ~${pStateFilter}`);
						}
						return pFilters;
					},
					GetRecordUserFacingID: (pBridge) => pBridge.StructureNumber || 'N/A',
				});
			// if the select2 plugin is not already initialized, initialize it now for the correct input type
			if (!window['jQuery'](this.getSelectInputHTMLID(pInput.Macro.RawHTMLID)).data('select2'))
			{
				window['jQuery'](this.getSelectInputHTMLID(pInput.Macro.RawHTMLID)).select2(tmpSelectOptions);
			}
		}
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.addLoadingSkeleton(pInput);
		// initialize the select2 plugin
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean}
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		//FIXME: base class doesn't allow this to be async - changed to run async safely for now, but not sure if this creates timing problems
		this._onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector).catch((pError) =>
		{
			this.pict.log.error(`Error in onDataMarshalToForm: ${pError}`);
		});
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @return {Promise<boolean>}
	 */
	async _onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector)
	{
		const tmpInputSelectElements = this.pict.ContentAssignment.getElement(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID));
		if (!tmpInputSelectElements || tmpInputSelectElements.length < 1)
		{
			return false;
		}
		// if we have a value (ID), resolve the Bridge for display (StructureNumber, Name)
		if (pValue && typeof pValue === 'string' || typeof pValue === 'number')
		{
			// Prefer the bridge cached in FormData (the document is a point-in-time record), so long
			// as it matches the selection.
			const tmpCachedBridge = this.pict.AppData?.DocumentData?.ReportData?.FormData?.Bridge;
			let tmpBridge;
			if (tmpCachedBridge && tmpCachedBridge.IDBridge > 0 && tmpCachedBridge.IDBridge == pValue)
			{
				tmpBridge = tmpCachedBridge;
			}
			else
			{
				tmpBridge = await this.pict.providers.BridgeProvider.getBridgeByID(pValue);
			}
			// build the prefilled value for the select2 dropdown
			const tmpOption = document.createElement('option');
			const tmpSelectElement = this.pict.ContentAssignment.getElement(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID))[0];
			if (!tmpSelectElement)
			{
				return false;
			}
			tmpOption.value = tmpBridge.IDBridge;
			tmpOption.text = `${tmpBridge.StructureNumber || 'N/A'}|~|${tmpBridge.Name || ('Unnamed Bridge #' + tmpBridge.IDBridge)}`;
			tmpOption.selected = true;
			tmpSelectElement.appendChild(tmpOption);
		}
		else
		{
			this.pict.log.info('No Bridge selected.');
		}
		// set the bridge selection callback for the Bridge Search Panel
		this.pict.views.HeadlightPaneBridges.setBridgeSelectionCallback((pBridge) =>
		{
			this.pict.log.info('Bridge selected:', pBridge.IDBridge);
			this.pict.log.info('Assigning bridge to the following input element:', pInput.Macro.RawHTMLID);
			// NOTE: ContentAssignment is failing to set this value for some reason, should investigate later
			document.getElementById(pInput.Macro.RawHTMLID)['value'] = pBridge.IDBridge;
			document.getElementById(pInput.Macro.RawHTMLID).dispatchEvent(new Event('change'));
		});
		// get the configuration for the current user to filter the Bridge search results (by State, etc.)
		this.pict.providers.ConfigurationProvider.getConfigurationByScope(this.pict.AppData?.CurrentUser?.CustomerID, (pConfig) =>
		{
			const tmpStateFilter = pConfig?.StateAbbreviation || null;
			this.initializeSelect2(pInput, tmpStateFilter);
			this.removeLoadingSkeleton(pInput);
		});
	}

	/**
	 * Handles the data request event for a select input in the PictProviderInputSelect class.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector object.
	 * @return {any} - The result of the onDataRequest method.
	 */
	onDataRequest(pView, pInput, pValue, pHTMLSelector)
	{
		const tmpInputSelectValue = this.pict.ContentAssignment.readContent(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID));
		this.pict.ContentAssignment.assignContent(pHTMLSelector, tmpInputSelectValue);
		pView.dataChanged(pInput.Hash);
		return super.onDataRequest(pView, pInput, tmpInputSelectValue, pHTMLSelector);
	}

	/**
	 * Handles the data change event for a select input in the PictProviderInputSelect class.
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector object.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {any} - The result of the onDataChange method.
	 */
	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		if (this.onChangeCallbacks.length > 0)
		{
			this.onChangeCallbacks.forEach((pCallback) =>
			{
				pCallback(pView, pInput, pValue, pHTMLSelector);
			});
		}
		return super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}
}

module.exports = CustomInputHandler;
